<template>
    <el-container class="list-page">
        <el-header class="list-page-header centered-list-search-header">
            <h1 class="list-page-title">{{$t('item.pageTitle')}}</h1>
            <div class="list-page-search">
                <el-input prefix-icon="el-icon-search" v-model="searchContent" :disabled="!contextLoaded"
                          :placeholder="$t('item.searchPlaceholder')"></el-input>
            </div>
            <div class="list-page-header-actions">
                <el-button class="list-page-export-button" :loading="exporting" :disabled="!contextLoaded || loading" @click="exportData">
                    <i class="el-icon-download"></i>
                    <span>{{$t('report.export')}}</span>
                </el-button>
                <el-button class="list-page-add-button" type="primary" :disabled="!contextLoaded || loading"
                           @click.native.prevent="goAddItem">
                    <i class="el-icon-plus"></i>
                    <span>{{$t('item.addItem')}}</span>
                </el-button>
            </div>
        </el-header>
        <el-main class="list-page-main" v-loading="loading">
            <el-alert v-if="loadError !== ''" :title="loadError" type="error" :closable="false" show-icon></el-alert>
            <template v-else-if="contextLoaded">
                <list-batch-actions :selected-count="selectedRows.length" :actions="batchActions"
                                    :loading-action="batchLoadingAction" @action="handleBatchAction"></list-batch-actions>
                <div class="list-table-card item-desktop-table">
                    <el-table ref="listTable" class="list-table" @sort-change="sortTable" :data="tableData"
                              :empty-text="$t('common.noData')"
                              :default-sort="{prop: 'itemDescription', order: 'descending'}"
                              row-key="innerUUID" @selection-change="handleSelectionChange"
                              @select-all="handleSelectAll" @row-click='handleRowHandle'>
                        <el-table-column type="selection" width="52" :reserve-selection="true"></el-table-column>
                        <el-table-column v-if="userSetting.isShowProduceCode === 1" prop="itemCode" :label="userSetting.codeLabel" min-width="160"></el-table-column>
                        <el-table-column prop="itemDescription" :label="userSetting.descriptionLabel" sortable="custom" :sort-orders="['descending', 'ascending']" min-width="320">
                            <template slot="header">
                                <span class="list-sort-header" :class="{'is-active': orderName === 'itemDescription'}">
                                    {{userSetting.descriptionLabel}}
                                    <svg v-if="orderName === 'itemDescription'" class="list-sort-icon is-active" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <path v-if="orderName === 'itemDescription' && orderTpe === 1" d="M8 6L12 2L16 6"/>
                                        <path v-else d="M8 18L12 22L16 18"/>
                                        <path d="M12 2V22"/>
                                    </svg>
                                </span>
                            </template>
                            <template slot-scope="scope">
                                <span class="list-description-cell">{{scope.row.itemDescription}}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="rate" :formatter="amountFormat" :label="userSetting.rateLabel" min-width="190" align="right" header-align="right"></el-table-column>
                        <el-table-column prop="inventoryNumber" :label="$t('item.columnInventoryCountUnit')" min-width="260" align="right" header-align="right">
                            <template slot-scope="scope">
                                <span>{{scope.row.inventoryNumber}}</span>
                                <span v-if="scope.row.unit"> {{scope.row.unit}}</span>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
                <div v-if="tableData.length === 0" class="item-mobile-empty">{{$t('common.noData')}}</div>
                <div v-else class="item-mobile-list">
                    <article v-for="item in tableData" :key="item.innerUUID" class="item-mobile-card"
                             role="button" tabindex="0" @click="openItem(item, $event)" @keyup.enter.self="openItem(item)"
                             @keyup.space.self.prevent="openItem(item)">
                        <el-checkbox class="item-mobile-checkbox" :value="isRowSelected(item)"
                                     :aria-label="userSetting.descriptionLabel + ': ' + item.itemDescription"
                                     @click.native.stop @change="toggleMobileSelection(item, $event)"></el-checkbox>
                        <div class="item-mobile-content">
                            <div class="item-mobile-heading">
                                <strong>{{item.itemDescription}}</strong>
                                <strong>{{amountFormat(item, {property: 'rate'})}}</strong>
                            </div>
                            <div class="item-mobile-facts">
                                <span v-if="userSetting.isShowProduceCode === 1"><small>{{userSetting.codeLabel}}</small>{{item.itemCode}}</span>
                                <span><small>{{$t('item.columnInventoryCountUnit')}}</small>{{item.inventoryNumber}}<template v-if="item.unit"> {{item.unit}}</template></span>
                            </div>
                        </div>
                    </article>
                </div>

                <div class="list-pagination">
                    <el-pagination
                            :key="'item-pagination-' + currentPage + '-' + pageSize"
                            @size-change="changeTotalCount"
                            @current-change="changePage"
                            :current-page="currentPage"
                            :page-sizes="[50, 100, 200, 400]"
                            :page-size="pageSize"
                            layout="total, sizes, prev, pager, next, jumper"
                            :total="totalCount">
                    </el-pagination>
                </div>
            </template>
        </el-main>
    </el-container>
</template>

<script>
    import {debounce} from '@/utils/debounce'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {readListPagination, writeListPagination} from '@/utils/listPagination'
    import {readListQueryState, writeListQueryState} from '@/utils/listQueryState'
    import {syncCompanySelection} from '@/utils/company'
    import {downloadDataExport} from '@/utils/dataExport'
    import {openListDetail} from '@/utils/listNavigation'
    import ListBatchActions from '@/components/common/ListBatchActions'

    export default {
        components: {
            ListBatchActions
        },
        computed: {
            batchActions() {
                return [
                    {key: 'delete', label: this.$t('common.delete'), icon: 'el-icon-delete', danger: true}
                ];
            }
        },
        methods: {
            buildListParams() {
                if (this.company == null) {
                    throw new Error('Current company is required');
                }
                return {
                    currentPage: this.currentPage,
                    pageSize: this.pageSize,
                    userWebCommonQuery: this.searchContent,
                    orderName: this.orderName,
                    orderTpe: this.orderTpe,
                    delStatus: 0,
                    companyUUID: this.company.innerUUID
                };
            },
            exportData() {
                if (!this.contextLoaded || this.loading || this.exporting) {
                    return;
                }
                this.exporting = true;
                return downloadDataExport(this.$axios, 'ITEM', this.buildListParams()).catch(() => {
                    this.$message.error(this.$t('common.systemError'));
                }).finally(() => {
                    this.exporting = false;
                });
            },
            handleSelectionChange(selection) {
                this.selectedRows = selection;
            },
            handleSelectAll(selection) {
                const selectedUUIDSet = new Set(selection.map(row => row.innerUUID));
                const allCurrentPageSelected = this.tableData.length > 0
                    && this.tableData.every(row => selectedUUIDSet.has(row.innerUUID));
                if (!allCurrentPageSelected) {
                    this.clearSelection();
                }
            },
            isRowSelected(row) {
                return this.selectedRows.some(selected => selected.innerUUID === row.innerUUID);
            },
            toggleMobileSelection(row, selected) {
                if (this.$refs.listTable) {
                    this.$refs.listTable.toggleRowSelection(row, selected);
                }
            },
            clearSelection() {
                this.selectedRows = [];
                if (this.$refs.listTable) {
                    this.$refs.listTable.clearSelection();
                }
            },
            handleBatchAction() {
                this.$confirm(this.$t('common.batchDeleteConfirm', {count: this.selectedRows.length}), this.$t('common.warning'), {
                    confirmButtonText: this.$t('common.ok'),
                    cancelButtonText: this.$t('common.cancel'),
                    type: 'warning'
                }).then(() => this.deleteSelectedItems()).catch(() => {});
            },
            deleteSelectedItems() {
                if (!this.contextLoaded || this.company == null || this.batchLoadingAction !== '') {
                    return;
                }
                this.batchLoadingAction = 'delete';
                return this.$axios.post('web_batch_action', {
                    params: {
                        entityType: 'ITEM',
                        action: 'DELETE',
                        companyUUID: this.company.innerUUID,
                        innerUUIDList: this.selectedRows.map(row => row.innerUUID)
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.$message.success(this.$t('common.batchActionSuccess', {count: response.data.processedCount}));
                        this.moveToExistingPage(response.data.processedCount);
                        this.clearSelection();
                        this.queryList();
                    }
                }).finally(() => {
                    this.batchLoadingAction = '';
                });
            },
            moveToExistingPage(deletedCount) {
                const remainingCount = Math.max(0, this.totalCount - deletedCount);
                const lastPage = Math.max(1, Math.ceil(remainingCount / this.pageSize));
                if (this.currentPage > lastPage) {
                    this.currentPage = lastPage;
                    this.saveListPagination();
                }
            },
            restoreListPagination() {
                const pagination = readListPagination('item');
                if (pagination != null) {
                    this.currentPage = pagination.currentPage;
                    this.pageSize = pagination.pageSize;
                }
            },
            restoreListQueryState() {
                const queryState = readListQueryState('item');
                if (queryState == null) {
                    return;
                }
                this.currentPage = queryState.currentPage;
                this.pageSize = queryState.pageSize;
                this.searchContent = queryState.searchContent;
                this.orderName = queryState.orderName;
                this.orderTpe = queryState.orderTpe;
            },
            saveListQueryState() {
                writeListQueryState('item', {
                    currentPage: this.currentPage,
                    pageSize: this.pageSize,
                    searchContent: this.searchContent,
                    orderName: this.orderName,
                    orderTpe: this.orderTpe
                });
            },
            saveListPagination() {
                writeListPagination('item', this.currentPage, this.pageSize);
            },
            goAddItem() {
                if (!this.contextLoaded || this.loading) {
                    return;
                }
                this.$router.push('/itemCreate');
            },
            queryList() {
                if (!this.contextLoaded || this.company == null) {
                    return Promise.reject(new Error('Current company is required'));
                }
                const requestSequence = ++this.listRequestSequence;
                this.loading = true;
                this.loadError = "";
                let that = this;
                return this.$axios.post('query_item_list_user_web', {
                    params: this.buildListParams()
                }).then(response => {
                    if (requestSequence !== that.listRequestSequence) {
                        return;
                    }
                    if (response.data.errorCode !== 0) {
                        throw new Error('Item list query failed');
                    }
                    const itemUUIDSet = new Set();
                    const dataList = Array.isArray(response.data.dataList) ? response.data.dataList : [];
                    that.tableData = dataList.filter(item => {
                        if (item == null
                            || (item.innerUUID != null && itemUUIDSet.has(item.innerUUID))
                            || (item.companyUUID != null && item.companyUUID !== that.company.innerUUID)
                            || Number(item.syncStatus) === 1) {
                            return false;
                        }
                        if (item.innerUUID != null && item.innerUUID !== '') {
                            itemUUIDSet.add(item.innerUUID);
                        }
                        return true;
                    });
                    const totalCount = Number(response.data.totalCount);
                    that.totalCount = Number.isFinite(totalCount) && totalCount >= 0
                        ? Math.floor(totalCount) : that.tableData.length;
                    that.loading = false;
                }).catch(() => {
                    if (requestSequence === that.listRequestSequence) {
                        that.loading = false;
                        that.loadError = that.$t('common.loadFailed');
                    }
                })
            },

            sortTable(column) {
                if (!this.contextLoaded || this.loading) {
                    return;
                }
                this.clearSelection();
                if (!column.order) {
                    this.orderName = "";
                    this.orderTpe = "";
                    this.queryList();
                    return;
                }
                this.orderName = column.prop;
                if (column.order === "ascending") {
                    this.orderTpe = 1
                } else if (column.order === "descending") {
                    this.orderTpe = 2
                }
                this.queryList();
            },
            changePage(val) {
                if (!this.contextLoaded || this.loading) {
                    return;
                }
                this.currentPage = val;
                this.saveListPagination();
                this.queryList();
            },
            changeTotalCount(val) {
                if (!this.contextLoaded || this.loading) {
                    return;
                }
                this.currentPage = 1;
                this.pageSize = val;
                this.saveListPagination();
                this.queryList();
            },
            handleRowHandle(row, column, event) {
                if (!this.contextLoaded || this.loading) {
                    return false;
                }
                //点击每一条列表数据触发的方法
                if (column && column.type === "selection") {
                    return false;
                }
                this.openItem(row, event);
            },
            openItem(row, event) {
                if (!this.contextLoaded || this.loading) {
                    return;
                }
                openListDetail(this.$router, {path: 'itemUpdate', query: {innerUUID: row.innerUUID}}, event);
            },
            //金钱格式化,增加金钱符号和分隔符
            amountFormat: function (row, column) {
                var columnAmount = row[column.property];
                if (columnAmount == undefined) {
                    return "";
                }
                return xiaoshudianfenge(columnAmount, this.userSetting);
            }
        },
        data() {
            return {
                tableData: [],
                loading: true,
                loadError: "",
                listRequestSequence: 0,
                contextLoaded: false,
                company: null,
                currentPage: 1,
                totalCount: 0,
                pageSize: 50,
                searchContent: "",
                selectedRows: [],
                batchLoadingAction: '',
                exporting: false,
                orderName: "itemDescription",
                orderTpe: 2,
                userSetting: null
            }
        },
        async mounted() {
            this.restoreListPagination();
            try {
                const response = await this.$axios.post('query_setting_and_company', {params: {}});
                if (response.data.errorCode !== 0) {
                    throw new Error('Company and setting query failed');
                }
                const selectedData = syncCompanySelection(response.data);
                if (selectedData == null) {
                    throw new Error('Selected company data is missing');
                }
                this.company = selectedData.company;
                this.userSetting = selectedData.setting;
                this.restoreListQueryState();
                this.contextLoaded = true;
                await this.queryList();
            } catch {
                this.loading = false;
                this.loadError = this.$t('common.loadFailed');
            }
            this.$watch('searchContent', debounce(() => {
                if (!this.contextLoaded) {
                    return;
                }
                this.clearSelection();
                this.currentPage = 1;
                this.saveListPagination();
                this.queryList();
            }, 500));
        },
        beforeRouteLeave(to, from, next) {
            this.saveListQueryState();
            next();
        }
    }
</script>
<style scoped>
    .list-description-cell {
        display: -webkit-box;
        max-height: 110px;
        overflow: hidden;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 5;
    }

    .item-mobile-list, .item-mobile-empty { display: none; }
    @media (max-width: 720px) {
        .item-desktop-table { display: none; }
        .item-mobile-list { display: grid; gap: 12px; }
        .item-mobile-empty {
            display: block; padding: 42px 16px; border: 1px dashed #d8dee8; border-radius: 14px;
            background: #fff; color: #8a97aa; text-align: center;
        }
        .item-mobile-card {
            display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 14px; padding: 16px;
            border: 1px solid #e6eaf0; border-radius: 14px; background: #fff; color: #263653;
            box-shadow: 0 6px 18px rgba(42, 61, 92, .06); cursor: pointer;
        }
        .item-mobile-card:focus-visible { outline: 3px solid rgba(64, 158, 255, .28); outline-offset: 2px; }
        .item-mobile-checkbox { padding-top: 2px; }
        .item-mobile-content { min-width: 0; }
        .item-mobile-heading { display: flex; justify-content: space-between; gap: 14px; }
        .item-mobile-heading strong { min-width: 0; overflow-wrap: anywhere; }
        .item-mobile-heading strong:first-child {
            display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 3;
        }
        .item-mobile-heading strong:last-child { flex: none; font-variant-numeric: tabular-nums; }
        .item-mobile-facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-top: 14px; }
        .item-mobile-facts span { min-width: 0; overflow-wrap: anywhere; }
        .item-mobile-facts small { display: block; margin-bottom: 4px; color: #8a97aa; font-size: 11px; font-weight: 700; text-transform: uppercase; }
    }
</style>
